import { useEffect, useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import {
  WORKINGTIME_HEADER,
  WORKINGTIME_MODAL_FIELDS,
} from '../../../constants/workingtime';
import {
  EmptyWorkingtimeGetResponse,
  WorkingtimeGetResponse,
} from '../../../types/workingtime/WorkingtimeGetResponse';
import { WorkingtimeCsvPreviewItem } from '../../../types/workingtime/WorkingtimeCsvPreviewItem';
import {
  getWorkingtimeList,
  exportWorkingtimeCsv,
} from '../../../services/api';
import { Col, Row } from 'react-bootstrap';
import RenderWorkingtimeRow from './RenderWorkingtimeRow';
import TablePagination from '../../../components/table/pagination/TablePagination';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  DefaultWorkingtimeGetQueryParams,
  WorkingtimeGetQueryParams,
} from '../../../types/workingtime/WorkingtimeGetQueryParams';
import { SortOrder } from '../../../types/sort/sort';
import {
  WORKINGTIME_DEFAULT_SORT_ORDER,
  WORKINGTIME_DEFAULT_SORT_COLUMN,
} from '../../../constants/sort';
import CsvModal from '../../../components/modal/CsvModal';
import NameSearch from '../../../components/search/NameSearch';
import YmPicker from '../../../components/picker/YmPicker';
import { format, set } from 'date-fns';
import { formatYmDisplay } from '../../../utils/formatYmDisplay';
import SearchResetButtons from '../../../components/button/SearchResetButtons';
import CsvExportButton from '../../../components/button/CsvExportButton';
import ReadOnlyTable from '../../../components/table/ReadOnlyTable';
import { saveBlobAsFile } from '../../../utils/saveBlobAsFile';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/WorkingtimeHeader.css';

const Workingtime = () => {
  const [getQueryParams, setGetQueryParams] =
    useState<WorkingtimeGetQueryParams>(DefaultWorkingtimeGetQueryParams);
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    WORKINGTIME_DEFAULT_SORT_ORDER
  );
  const [activeSortColumn, setActiveSortColumn] = useState<string>(
    WORKINGTIME_DEFAULT_SORT_COLUMN
  );
  const [employeeLabel, setEmployeeLabel] = useState<string | null>(null);
  const [csvItem, setCsvItem] = useState<WorkingtimeCsvPreviewItem | null>(
    null
  );
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const [controlsKey, setControlsKey] = useState(0);
  const [resetSignal, setResetSignal] = useState(0); // 検索リセット用のシグナル
  const queryClient = useQueryClient();
  const fmtYM = (d: Date | null | undefined) => (d ? format(d, 'yyyy-MM') : '');

  // テーブルの横スクロール要素（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: exportCsv } = useMutation({
    mutationKey: ['exportWorkingtimeCsv'],
    mutationFn: () => exportWorkingtimeCsv(getQueryParams),
  });

  // 検索のクエリパラメーターをセット
  const setQuery = (
    patch: {
      selectedMonth?: Date | null;
    } & Partial<Pick<WorkingtimeGetQueryParams, 'employeeId' | 'date'>>
  ) => {
    setGetQueryParams((prev) => {
      const next = {
        ...prev,
        ...patch,
        ...(patch.selectedMonth !== undefined
          ? { date: fmtYM(patch.selectedMonth) }
          : {}),
      } as WorkingtimeGetQueryParams;

      return next;
    });
  };

  // 一覧取得
  const {
    data: WorkingtimeGetResponse,
    isError,
    error,
  } = useQuery<WorkingtimeGetResponse>({
    queryKey: ['WorkingtimeList'],
    queryFn: () => getWorkingtimeList(getQueryParams),
    initialData: EmptyWorkingtimeGetResponse,
  });

  // ページ数、ソート順、ソートカラムが変わったら一覧を再取得
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['WorkingtimeList'],
    });
  }, [getQueryParams.page, getQueryParams.sortKey, getQueryParams.sortOrder]);

  // 一覧取得失敗時のアラート
  useEffect(() => {
    if (isError) {
      alert(error);
    }
  }, [isError]);

  // ソートの状態を管理
  const handleSort = (sortKey: string) => {
    if (sortKey === activeSortColumn) {
      // 選択中のカラムが現在のアクティブなカラムと同じ場合、ソート順を切り替える
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // 新しいカラムが選択された場合、アクティブなカラムを降順で更新
      setActiveSortColumn(sortKey);
      setSortOrder('desc');
    }
  };

  // ソートの状態が変わったときにクエリパラメータを更新
  useEffect(() => {
    setGetQueryParams((prev) => ({
      ...prev,
      sortOrder: sortOrder,
      sortKey: activeSortColumn,
    }));
  }, [sortOrder, activeSortColumn]);

  // CSV出力用のモーダル情報を作成
  const buildCsvItem = (): WorkingtimeCsvPreviewItem => {
    const monthDisp = formatYmDisplay(getQueryParams.date) || '全期間';

    const employeeName =
      getQueryParams.employeeId != null ? (employeeLabel ?? '') : '全社員';

    return { employeeName, date: monthDisp };
  };

  const onCsvButtonClick = async () => {
    setCsvItem(buildCsvItem());
    setShowCsvModal(true);
  };

  const onCsvModalSubmitButtonClick = async () => {
    try {
      const { blob, filename } = await exportCsv();

      const fallback = 'Workingtimes.csv';
      const resolved =
        filename && filename.trim() ? decodeURIComponent(filename) : fallback;

      const ok = await saveBlobAsFile(blob, resolved);
      if (ok) alert('CSVファイルの保存が完了しました');
    } catch (err: any) {
      let msg = '';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        if (json?.message) msg = json.message;
      }
      alert(msg);
    }
  };

  return (
    <PageContainer title="稼働管理">
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-workingtime gx-0">
          {/* 左：検索条件（縮めない） */}
          <Col className="header-fields-workingtime order-1">
            <NameSearch
              key={`employee-${controlsKey}`}
              setSelectedId={(e: number) => setQuery({ employeeId: e })}
              onSelectedLabel={(label) => setEmployeeLabel(label)}
              resetSignal={resetSignal}
            />
            <YmPicker
              placeholder="期間を選択"
              selectedMonth={selectedMonth}
              setSelectedMonth={(d) => {
                setSelectedMonth(d);
                setQuery({ selectedMonth: d });
              }}
            />
          </Col>

          {/* 右：ボタン群（検索/リセット固定 + CSVは折り返し対象） */}
          <Col xs="auto" className="header-right-workingtime order-2">
            <div className="actions-group-workingtime">
              <div className="actions-searchreset-workingtime">
                <SearchResetButtons
                  onSearch={async () => {
                    // 検索ボタン押下時はページを1に戻す
                    setGetQueryParams((prev) => ({ ...prev, page: 1 }));
                    const paramsForSearch = {
                      ...getQueryParams,
                      page: 1,
                    };
                    const data = await queryClient.fetchQuery({
                      queryKey: ['WorkingtimeList'],
                      queryFn: () => getWorkingtimeList(paramsForSearch),
                    });
                    if (!data.items || data.items.length === 0) {
                      alert('該当データが存在しません');
                    }
                  }}
                  onReset={() => {
                    setSelectedMonth(null);
                    setEmployeeLabel(null);
                    setGetQueryParams(DefaultWorkingtimeGetQueryParams);
                    setControlsKey((k) => k + 1);
                    queryClient.fetchQuery({
                      queryKey: ['WorkingtimeList'],
                      queryFn: () =>
                        getWorkingtimeList(DefaultWorkingtimeGetQueryParams),
                    });
                    setResetSignal((s) => s + 1);
                  }}
                />
              </div>
              <div className="actions-extra-workingtime">
                <CsvExportButton
                  onClick={onCsvButtonClick}
                  buttonLabel="CSV出力"
                />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <ReadOnlyTable
        headers={WORKINGTIME_HEADER}
        items={WorkingtimeGetResponse.items}
        renderItem={(item) => <RenderWorkingtimeRow item={item} />}
        onSort={handleSort}
        sortOrder={sortOrder}
        activeSortColumn={activeSortColumn}
        actionable={false}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
      />

      <TablePagination
        totalPages={WorkingtimeGetResponse.totalPages}
        onPageChange={(page) =>
          setGetQueryParams((prev) => ({ ...prev, page }))
        }
        ref={footerRef}
      />

      {showCsvModal && csvItem && (
        <CsvModal<WorkingtimeCsvPreviewItem>
          show={showCsvModal}
          item={csvItem}
          fields={WORKINGTIME_MODAL_FIELDS}
          saveButtonLabel="OK"
          onClose={() => {
            setShowCsvModal(false);
            setCsvItem(null);
          }}
          onConfirm={async () => {
            await onCsvModalSubmitButtonClick();
            setShowCsvModal(false);
            setCsvItem(null);
          }}
        />
      )}
    </PageContainer>
  );
};

export default Workingtime;
